// P5 T4 — header notification bell + compact inbox panel. Rides the existing
// WS rail (document_notify) for a live badge; a 60s poll backs it up. Panel is
// a dropdown (not a page) per the v1 scope. Design: Industrial Core — tokens,
// zero-duration hover, uppercase panel header, clickable rows.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { WsClient } from "@/lib/ws-client";
import { useNotifications } from "../hooks/useNotifications";
import type { NotificationRecord } from "../api/client";
import styles from "./NotificationBell.module.css";

/** Compact relative-time label (e.g. "3m", "2h", "5d"). */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export function NotificationBell({ wsClient }: { wsClient: WsClient | null }) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications(wsClient);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onRowActivate = useCallback(
    (n: NotificationRecord) => {
      void markRead(n.id);
      setOpen(false);
      if (n.documentId) navigate(`/verify/${n.documentId}`);
    },
    [markRead, navigate],
  );

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.bellBtn}
        aria-label="Notifications"
        aria-expanded={open}
        title="Notifications"
        onClick={() => setOpen((v) => !v)}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className={styles.badge} data-testid="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>Notifications</span>
            {unreadCount > 0 && (
              <button type="button" className={styles.markAll} onClick={() => void markAllRead()}>
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className={styles.empty}>No notifications yet</div>
          ) : (
            <ul className={styles.list}>
              {notifications.map((n) => (
                <li key={n.id}>
                  <div
                    className={`${styles.row} ${n.readAt === null ? styles.unread : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onRowActivate(n)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowActivate(n);
                      }
                    }}
                  >
                    <div className={styles.rowMain}>
                      <span className={styles.rowMessage}>{n.message || "Notification"}</span>
                      {n.sourceRuleName && <span className={styles.rowRule}>{n.sourceRuleName}</span>}
                    </div>
                    <span className={styles.rowTime}>{timeAgo(n.createdAt)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
